import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 " +
    "disabled:pointer-events-none disabled:opacity-50 select-none",
  {
    variants: {
      variant: {
        // primary: violet gradient pill (the "Пополнить баланс" + send button vibe)
        primary:
          "bg-brand-gradient text-white shadow-pop hover:brightness-105 active:brightness-95",
        // secondary: white card pill with violet outline (e.g. sidebar 'Пополнить баланс')
        secondary:
          "bg-surface text-brand-700 border border-brand-200 hover:bg-brand-50 active:bg-brand-100",
        // ghost: transparent, used inside the sidebar nav
        ghost:
          "bg-transparent text-ink-mute hover:bg-surface-2 hover:text-ink",
        // active nav item
        nav:
          "bg-brand-50 text-brand-700 hover:bg-brand-100",
        // outline (subtle border, white bg)
        outline:
          "bg-surface text-ink border border-line hover:bg-surface-2",
        // destructive
        destructive:
          "bg-danger text-white hover:brightness-110",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-5 text-base",
        icon: "h-10 w-10 p-0",
        iconSm: "h-8 w-8 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
